// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: History on the "What Konvo knows" page: the last notes added, changed and forgotten.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {historyActionText, M, shortDate} from '@app/features/konvo_knows/components/KnowsMessages';
import styles from '@app/features/konvo_knows/components/KonvoKnows.module.css';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import type {KnowsHistoryEntry} from '@app/features/konvo_knows/utils/KnowsModel';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {
	ChatCircleIcon,
	ClockCounterClockwiseIcon,
	PencilSimpleIcon,
	PlusIcon,
	TrashIcon,
	UserIcon,
} from '@app/features/ui/icons/Icons';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

function ActionIcon({action}: {action: KnowsHistoryEntry['action']}) {
	switch (action) {
		case 'added':
			return <PlusIcon size={15} />;
		case 'changed':
			return <PencilSimpleIcon size={15} />;
		case 'forgot':
			return <TrashIcon size={15} />;
	}
}

export const KnowsHistoryModal = observer(() => {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoKnows.loadHistory();
	}, []);
	const history = KonvoKnows.history;
	return (
		<Modal.Root size="small" centered onClose={() => ModalCommands.pop()} data-flx="konvo-knows.history.root">
			<Modal.Header
				icon={<ClockCounterClockwiseIcon size={20} />}
				title={i18n._(M.historyTitle)}
				data-flx="konvo-knows.history.header"
			/>
			<Modal.Content data-flx="konvo-knows.history.content">
				<div className={styles.historyList} data-flx="konvo-knows.history.list">
					{history == null ? null : history.length === 0 ? (
						<p className={styles.quiet}>{i18n._(M.historyEmpty)}</p>
					) : (
						history.map((entry, index) => (
							<div
								key={`${entry.at}-${index}`}
								className={clsx(styles.historyRow, entry.action === 'forgot' && styles.historyForgot)}
								data-flx="konvo-knows.history.row"
							>
								<span className={styles.historyIcon} aria-hidden="true">
									<ActionIcon action={entry.action} />
								</span>
								<span className={styles.historyBody}>
									<span className={styles.historyText}>{entry.text}</span>
									<span className={styles.historyMeta}>
										{historyActionText(i18n, entry.action)} · {shortDate(entry.at)}
										{entry.source === 'chat' ? (
											<ChatCircleIcon size={12} className={styles.historySource} aria-label={i18n._(M.sourceChat)} />
										) : entry.source === 'you' ? (
											<UserIcon size={12} className={styles.historySource} aria-label={i18n._(M.sourceYou)} />
										) : null}
									</span>
								</span>
							</div>
						))
					)}
				</div>
			</Modal.Content>
		</Modal.Root>
	);
});

export function openKnowsHistory(): void {
	ModalCommands.push(modal(() => <KnowsHistoryModal />));
}

/** The History pill beside the page title (desktop header) and in the phone footer. */
export const KnowsHistoryButton = observer(() => {
	const {i18n} = useLingui();
	return (
		<button type="button" className={styles.pill} onClick={openKnowsHistory} data-flx="konvo-knows.history.open">
			<ClockCounterClockwiseIcon size={15} />
			{i18n._(M.history)}
		</button>
	);
});
